import { cn } from '../lib/util';

/* Hand-drawn style illustrations so the site feels crafted without
   relying on external stock photography. Colors pull from tokens. */

export function Steam({ className }: { className?: string }) {
  return (
    <div className={cn('pointer-events-none absolute flex gap-3', className)} aria-hidden>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: 6,
            height: 34,
            borderRadius: 999,
            background: 'linear-gradient(to top, transparent, color-mix(in srgb, var(--color-faint) 70%, transparent))',
            animation: `drift 3.4s ease-in-out ${i * 0.6}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

/* A plated parotta medallion — the hero centerpiece. */
export function ParottaPlate({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 400 400" className={className} role="img" aria-label="A plate of flaky South Indian parotta with curry" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="plate" cx="50%" cy="42%" r="60%">
          <stop offset="0%" stopColor="#fffdf9" />
          <stop offset="78%" stopColor="#fbf4e8" />
          <stop offset="100%" stopColor="#efe3ce" />
        </radialGradient>
        <linearGradient id="parotta" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#f4cd8a" />
          <stop offset="55%" stopColor="#dfa759" />
          <stop offset="100%" stopColor="#c6832f" />
        </linearGradient>
        <radialGradient id="curry" cx="50%" cy="40%" r="65%">
          <stop offset="0%" stopColor="#c8501a" />
          <stop offset="100%" stopColor="#8f2f0c" />
        </radialGradient>
      </defs>

      {/* plate */}
      <circle cx="200" cy="205" r="168" fill="url(#plate)" stroke="#e4d7bf" strokeWidth="2" />
      <circle cx="200" cy="205" r="150" fill="none" stroke="#eaddc6" strokeWidth="1.5" />

      {/* parotta — concentric coiled layers */}
      <g transform="translate(150 150)">
        <circle cx="0" cy="0" r="98" fill="url(#parotta)" stroke="#b9772a" strokeWidth="2" transform="translate(52 62)" />
        <g transform="translate(52 62)" fill="none" stroke="#b9772a" strokeOpacity="0.55" strokeWidth="2.4">
          <circle cx="0" cy="0" r="78" />
          <circle cx="0" cy="0" r="58" />
          <circle cx="0" cy="0" r="38" />
          <circle cx="0" cy="0" r="18" />
        </g>
        <g transform="translate(52 62)" stroke="#f6dca7" strokeOpacity="0.8" strokeWidth="1.2" fill="none">
          <circle cx="0" cy="0" r="88" />
          <circle cx="0" cy="0" r="68" />
          <circle cx="0" cy="0" r="48" />
        </g>
      </g>

      {/* curry bowl */}
      <g transform="translate(258 250)">
        <ellipse cx="0" cy="6" rx="60" ry="46" fill="#e9dcc4" />
        <ellipse cx="0" cy="0" rx="54" ry="40" fill="url(#curry)" />
        <ellipse cx="-16" cy="-12" rx="16" ry="9" fill="#e0692b" opacity="0.55" />
        <circle cx="18" cy="8" r="4" fill="#5f1c07" />
        <circle cx="-6" cy="16" r="3" fill="#5f1c07" />
        {/* curry leaf */}
        <path d="M 30 -26 q 14 -10 26 -2 q -12 8 -26 2 Z" fill="#3f6b3a" />
      </g>

      {/* scattered spices */}
      <g fill="#7a1f12">
        <circle cx="96" cy="286" r="4" />
        <circle cx="120" cy="300" r="3" />
        <circle cx="300" cy="150" r="3.5" />
      </g>
      <g fill="#b9862e">
        <circle cx="110" cy="120" r="3" />
        <circle cx="288" cy="316" r="3.5" />
      </g>
    </svg>
  );
}

/* Small decorative spice-mandala used as a section flourish. */
export function Mandala({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 120 120" className={className} aria-hidden xmlns="http://www.w3.org/2000/svg">
      <g fill="none" stroke="currentColor" strokeWidth="1.4">
        <circle cx="60" cy="60" r="10" />
        {Array.from({ length: 12 }).map((_, i) => {
          return (
            <g key={i} transform={`rotate(${i * 30} 60 60)`}>
              <path d={`M 60 44 q 8 -12 0 -26 q -8 14 0 26`} />
              <circle cx="60" cy="14" r="2.4" fill="currentColor" stroke="none" />
            </g>
          );
        })}
      </g>
    </svg>
  );
}
